import RTSPServerForm, { rtspServerDefaults } from "../components/RTSPServerForm";
import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  Tooltip,
  IconButton
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useAuth } from "../context/AuthContext";
import LicenseCtrl from "../components/LicenseCtrl";
import PwaForm from "../components/PwaForm";
import WebRtcForm from "../components/WebRtcForm";
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SecurityForm from "../components/SecurityForm";
import AboutForm from "../components/AboutForm";
import ServerForm from "../components/ServerForm";
import MapForm from "../components/MapForm";
import AnalyticsForm from "../components/AnalyticsForm";
import PreviewCfgForm from "../components/PreviewCfgForm";
import { isDesktopShellEnvironment } from "../utils/desktopShell";
import { openExternalPath } from "../utils/externalLinks";
import { DEFAULT_MAP_TILE_LAYERS } from "../map/tileLayers";

type MapRuntimeConfig = {
  tileLayers: string;
  enableOffline: boolean;
  offlineLicensed?: boolean;
};

type AnalyticsRuntimeConfig = {
  enabled: boolean;
  dsn: string;
  host: string;
  port: number;
  database: string;
  user: string;
  password: string;
  sslMode: string;
  dataRetentionDays: number;
  dataCompressionDays: number;
};

const defaultMapConfig: MapRuntimeConfig = {
  tileLayers: JSON.stringify(DEFAULT_MAP_TILE_LAYERS, null, 2),
  enableOffline: false,
  offlineLicensed: false
};

const defaultAnalyticsConfig: AnalyticsRuntimeConfig = {
  enabled: false,
  dsn: "",
  host: "localhost",
  port: 5432,
  database: "skyview",
  user: "skyview",
  password: "skyview",
  sslMode: "disable",
  dataRetentionDays: 30,
  dataCompressionDays: 3
};

const isTileLayersJson = (value?: string | null): boolean => {
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return false;
  }
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed);
  } catch {
    return false;
  }
};

export type RuntimeConfig = {
  rtspServer: typeof rtspServerDefaults;
  serverName: string;
  listenAddress: string;
  corsEnabled: boolean;
  registrationOpen: boolean;
  enableUserManagement: boolean;
  enableErrorDetection: boolean;
  enableSystemMonitor: boolean;
  autostartStreams: boolean;
  iceServersJson: string;
  loopbackIceCandidate: boolean;
  keepHostCandidates: boolean;
  fullIce: boolean;
  forceRelay: boolean;
  publicIp: string;
  nat1to1Enabled: boolean;
  ephemeralUdpPortRangeEnabled: boolean;
  ephemeralUdpPortRange: {
    min: number;
    max: number;
  };
  playbackAcceleration: "auto" | "software";
  previewAcceleration: "auto" | "software";
  alwaysTranscodeH265ToH264: boolean;
  enableWebRtcDebug: boolean;
  token: string;
  accessTokenTtlMinutes: number;
  caCertPath: string;
  enablePreview: boolean;
  previewWorkers: number;
  previewMemoryBudgetMiB: number;
  previewFramerate: number;
  recastPreviewIntervalMs: number;
  previewWidth: number;
  previewHeight: number;
  previewCaptureTimeoutMs: number;
  previewTargetWarmupGraceMs: number;
  previewBackoffMinMs: number;
  previewBackoffMaxMs: number;
  map: MapRuntimeConfig;
  analytics: AnalyticsRuntimeConfig;
};

type TabPanelProps = {
  children: ReactNode;
  index: number;
  value: number;
};

const defaultRuntimeConfig: RuntimeConfig = {
  rtspServer: rtspServerDefaults,
  serverName: "SkyView",
  listenAddress: ":8100",
  corsEnabled: true,
  registrationOpen: true,
  enableUserManagement: true,
  enableErrorDetection: true,
  enableSystemMonitor: true,
  autostartStreams: true,
  iceServersJson: JSON.stringify([{ urls: ["stun:stun.l.google.com:19302"] }], null, 2),
  loopbackIceCandidate: true,
  keepHostCandidates: true,
  fullIce: true,
  forceRelay: false,
  publicIp: "",
  nat1to1Enabled: false,
  ephemeralUdpPortRangeEnabled: false,
  ephemeralUdpPortRange: {
    min: 0,
    max: 0
  },
  playbackAcceleration: "auto",
  previewAcceleration: "auto",
  alwaysTranscodeH265ToH264: true,
  enableWebRtcDebug: false,
  token: "",
  accessTokenTtlMinutes: 30,
  caCertPath: "data/caddy/pki/authorities/local/root.crt",
  enablePreview: true,
  previewWorkers: 6,
  previewMemoryBudgetMiB: 512,
  previewFramerate: 1,
  recastPreviewIntervalMs: 5000,
  previewWidth: 320,
  previewHeight: 180,
  previewCaptureTimeoutMs: 12000,
  previewTargetWarmupGraceMs: 15000,
  previewBackoffMinMs: 1000,
  previewBackoffMaxMs: 30000,
  map: { ...defaultMapConfig },
  analytics: { ...defaultAnalyticsConfig }
};

const caCertPathPattern = /^(?!.*[<>:"|?*])(?!.*\/\/)(?:\.{1,2}\/|\/)?(?:[A-Za-z0-9._-]+\/)*[A-Za-z0-9._-]+\.(?:crt|pem)$/i;
const UDP_PORT_MIN = 1024;
const UDP_PORT_MAX = 65535;

const userManagementLicenseError = "User management requires the Users license option or demo mode.";
const errorDetectionLicenseError = "Error detection requires the ErrorDetection license option or demo mode.";
const offlineMapLicenseError = "Offline maps require the OfflineMap license option.";
const analyticsLicenseError = "Analytics requires the Analytics license option.";

const extractListenPort = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  if (/^:\d+$/u.test(trimmed)) {
    return Number.parseInt(trimmed.slice(1), 10);
  }

  const bracketedIpv6 = trimmed.match(/^\[[^\]]+\]:(\d+)$/u);
  if (bracketedIpv6?.[1]) {
    return Number.parseInt(bracketedIpv6[1], 10);
  }

  const separator = trimmed.lastIndexOf(":");
  if (separator <= 0 || separator === trimmed.length - 1) {
    return null;
  }

  const host = trimmed.slice(0, separator).trim();
  const portCandidate = trimmed.slice(separator + 1).trim();
  if (host.length === 0 || !/^\d+$/u.test(portCandidate)) {
    return null;
  }

  return Number.parseInt(portCandidate, 10);
};

const normalizeRuntimeConfig = (config?: Partial<RuntimeConfig>): RuntimeConfig => {
  const offlineLicensed = Boolean(config?.map?.offlineLicensed);
  const normalizedMap: MapRuntimeConfig = {
    tileLayers: typeof config?.map?.tileLayers === "string" && config.map.tileLayers.trim().length > 0
      ? config.map.tileLayers
      : defaultMapConfig.tileLayers,
    enableOffline: offlineLicensed ? (config?.map?.enableOffline ?? defaultMapConfig.enableOffline) : false,
    offlineLicensed
  };

  const normalizedAnalytics: AnalyticsRuntimeConfig = {
    enabled: Boolean(config?.analytics?.enabled),
    dsn: (config?.analytics?.dsn ?? "").trim(),
    host: (config?.analytics?.host ?? defaultAnalyticsConfig.host).trim() || defaultAnalyticsConfig.host,
    port: typeof config?.analytics?.port === "number" && config.analytics.port > 0
      ? config.analytics.port
      : defaultAnalyticsConfig.port,
    database: (config?.analytics?.database ?? defaultAnalyticsConfig.database).trim() || defaultAnalyticsConfig.database,
    user: (config?.analytics?.user ?? defaultAnalyticsConfig.user).trim() || defaultAnalyticsConfig.user,
    password: (config?.analytics?.password ?? defaultAnalyticsConfig.password).trim(),
    sslMode: (config?.analytics?.sslMode ?? defaultAnalyticsConfig.sslMode).trim() || defaultAnalyticsConfig.sslMode,
    dataRetentionDays: typeof config?.analytics?.dataRetentionDays === "number" && config.analytics.dataRetentionDays > 0
      ? config.analytics.dataRetentionDays
      : defaultAnalyticsConfig.dataRetentionDays,
    dataCompressionDays: typeof config?.analytics?.dataCompressionDays === "number" && config.analytics.dataCompressionDays >= 0
      ? config.analytics.dataCompressionDays
      : defaultAnalyticsConfig.dataCompressionDays
  };

  const merged: RuntimeConfig = {
    ...defaultRuntimeConfig,
    ...config,
    rtspServer: { ...rtspServerDefaults, ...config?.rtspServer },
    autostartStreams: config?.autostartStreams ?? defaultRuntimeConfig.autostartStreams,
    enableErrorDetection: config?.enableErrorDetection ?? defaultRuntimeConfig.enableErrorDetection,
    enableSystemMonitor: config?.enableSystemMonitor ?? defaultRuntimeConfig.enableSystemMonitor,
    enablePreview: config?.enablePreview ?? defaultRuntimeConfig.enablePreview,
    map: normalizedMap
  };
  const trimmedServerName = (config?.serverName ?? "").trim();
  merged.serverName = trimmedServerName.length > 0 ? trimmedServerName : defaultRuntimeConfig.serverName;

  if (!config?.accessTokenTtlMinutes || config.accessTokenTtlMinutes <= 0) {
    merged.accessTokenTtlMinutes = defaultRuntimeConfig.accessTokenTtlMinutes;
  }
  if (!config?.caCertPath) {
    merged.caCertPath = defaultRuntimeConfig.caCertPath;
  }
  if (!config?.previewMemoryBudgetMiB || config.previewMemoryBudgetMiB <= 0) {
    merged.previewMemoryBudgetMiB = defaultRuntimeConfig.previewMemoryBudgetMiB;
  }
  if (!config?.previewWorkers || config.previewWorkers <= 0) {
    merged.previewWorkers = defaultRuntimeConfig.previewWorkers;
  }
  if (!config?.recastPreviewIntervalMs || config.recastPreviewIntervalMs <= 0) {
    merged.recastPreviewIntervalMs = defaultRuntimeConfig.recastPreviewIntervalMs;
  }
  if (!config?.previewFramerate || config.previewFramerate <= 0) {
    merged.previewFramerate = defaultRuntimeConfig.previewFramerate;
  }
  if (!config?.previewWidth || config.previewWidth <= 0) {
    merged.previewWidth = defaultRuntimeConfig.previewWidth;
  }
  if (!config?.previewHeight || config.previewHeight <= 0) {
    merged.previewHeight = defaultRuntimeConfig.previewHeight;
  }
  if (!config?.previewCaptureTimeoutMs || config.previewCaptureTimeoutMs <= 0) {
    merged.previewCaptureTimeoutMs = defaultRuntimeConfig.previewCaptureTimeoutMs;
  }
  if (!config?.previewTargetWarmupGraceMs || config.previewTargetWarmupGraceMs <= 0) {
    merged.previewTargetWarmupGraceMs = defaultRuntimeConfig.previewTargetWarmupGraceMs;
  }
  if (!config?.previewBackoffMinMs || config.previewBackoffMinMs <= 0) {
    merged.previewBackoffMinMs = defaultRuntimeConfig.previewBackoffMinMs;
  }
  if (!config?.previewBackoffMaxMs || config.previewBackoffMaxMs <= 0) {
    merged.previewBackoffMaxMs = defaultRuntimeConfig.previewBackoffMaxMs;
  }
  if (merged.previewBackoffMaxMs < merged.previewBackoffMinMs) {
    merged.previewBackoffMaxMs = merged.previewBackoffMinMs;
  }

  const range = config?.ephemeralUdpPortRange;
  merged.ephemeralUdpPortRangeEnabled = config?.ephemeralUdpPortRangeEnabled ?? defaultRuntimeConfig.ephemeralUdpPortRangeEnabled;
  merged.ephemeralUdpPortRange = {
    min: typeof range?.min === "number" ? range.min : defaultRuntimeConfig.ephemeralUdpPortRange.min,
    max: typeof range?.max === "number" ? range.max : defaultRuntimeConfig.ephemeralUdpPortRange.max
  };

  merged.analytics = normalizedAnalytics;

  return merged;
};

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`config-tabpanel-${index}`}
    aria-labelledby={`config-tab-${index}`}
    sx={{ pt: 2 }}
  >
    {value === index && children}
  </Box>
);

const ConfigPage = () => {
  const { authorizedFetch, user, userManagementEnabled } = useAuth();
  const [tab, setTab] = useState(0);
  const isDesktopShell = useMemo(() => isDesktopShellEnvironment(), []);
  const [initialConfig, setInitialConfig] = useState<RuntimeConfig>(defaultRuntimeConfig);
  const [status, setStatus] = useState<{ saved: boolean; error: string | null }>(
    { saved: false, error: null }
  );
  const [restartStatus, setRestartStatus] = useState<{ pending: boolean; error: string | null }>({ pending: false, error: null });
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);
  const optionalUdpPortField = yup.number()
    .typeError("Port must be a number")
    .integer("Port must be a whole number")
    .test("port-range-bounds", `Port must be between ${UDP_PORT_MIN} and ${UDP_PORT_MAX}`, (value) => {
      if (value === undefined || value === null || value === 0) return true;
      return value >= UDP_PORT_MIN && value <= UDP_PORT_MAX;
    });
  const requiredUdpPortField = yup.number()
    .typeError("Port must be a number")
    .integer("Port must be a whole number")
    .min(UDP_PORT_MIN, `Port must be between ${UDP_PORT_MIN} and ${UDP_PORT_MAX}`)
    .max(UDP_PORT_MAX, `Port must be between ${UDP_PORT_MIN} and ${UDP_PORT_MAX}`)
    .required("Port is required");

  const formik = useFormik<RuntimeConfig>({
    validationSchema: yup.object().shape({
      listenAddress: yup.string()
        .required("Listen address is required")
        .test("is-valid-port", "Port must be between 1 and 65535", (value) => {
          if (!value) return false;
          const port = extractListenPort(value);
          if (port === null) return false;
          return port >= 1 && port <= 65535;
        }),
      publicIp: yup.string().when("nat1to1Enabled", (nat1to1Enabled: any, schema: any) => {
        const ipvTest = (value: unknown) => {
          if (typeof value !== "string" || value.length === 0) return false;
          const ipv4 = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
          const ipv6 = /^(?:[A-F0-9]{1,4}:){7}[A-F0-9]{1,4}$/i;
          return ipv4.test(value) || ipv6.test(value);
        };

        const reservedTest = (value: unknown) => {
          if (typeof value !== "string" || value.length === 0) return false;
          const parts = value.split(".");
          if (parts.length === 4) {
            const first = Number(parts[0]);
            const second = Number(parts[1]);
            if (Number.isNaN(first) || Number.isNaN(second)) return false;

            // 0.0.0.0/8
            if (first === 0) return false;
            // Loopback 127.0.0.0/8
            if (first === 127) return false;
            // Multicast 224.0.0.0/4 (224-239)
            if (first >= 224 && first <= 239) return false;
            // Reserved/experimental 240.0.0.0/4 (240-255)
            if (first >= 240 && first <= 255) return false;
            // 192.0.0.0/24 specifically
            if (first === 192 && second === 0) return false;
          }
          // IPv6 multicast ff00::/8
          if (/^ff/i.test(value)) return false;
          return true;
        };

        // Allow empty string — only validate non-empty values
        return schema.test("is-ip", "Must be a valid IPv4 or IPv6 address", (value: unknown) => {
          if (value === undefined || value === null || value === "") return true;
          return ipvTest(value);
        }).test("not-allowed-range", "Address is in a disallowed range (loopback, multicast, reserved or special)", (value: unknown) => {
          if (value === undefined || value === null || value === "") return true;
          return reservedTest(value);
        });
      }),
      ephemeralUdpPortRangeEnabled: yup.boolean(),
      ephemeralUdpPortRange: yup.object({
        min: optionalUdpPortField,
        max: optionalUdpPortField
      }).when("ephemeralUdpPortRangeEnabled", {
        is: true,
        then: (schema: any) =>
          schema
            .shape({
              min: requiredUdpPortField,
              max: requiredUdpPortField
            })
            .test("min-lte-max", "Maximum port must be greater than or equal to minimum port", function (this: yup.TestContext, value: { min: number; max: number } | undefined) {
              if (!value) return true;
              if (typeof value.min !== "number" || typeof value.max !== "number") {
                return true;
              }
              if (value.max < value.min) {
                return this.createError({
                  path: `${this.path}.max`,
                  message: "Maximum port must be greater than or equal to minimum port"
                });
              }
              return true;
            }),
        otherwise: (schema: any) =>
          schema.test("optional-range-valid", "Maximum port must be greater than or equal to minimum port", function (this: yup.TestContext, value: { min: number; max: number } | undefined) {
            if (!value) return true;
            if (!value.min || !value.max) return true;
            if (value.max < value.min) {
              return this.createError({
                path: `${this.path}.max`,
                message: "Maximum port must be greater than or equal to minimum port"
              });
            }
            return true;
          })
      }),
      accessTokenTtlMinutes: yup.number()
        .typeError("Access token TTL must be a number")
        .when([], {
          is: () => !isDesktopShell,
          then: (schema) => schema
            .integer("Access token TTL must be a whole number of minutes")
            .min(10, "Access token TTL must be at least 10 minutes")
            .max(1200, "Access token TTL cannot exceed 1200 minutes")
            .required("Access token TTL is required"),
          otherwise: (schema) => schema
        }),
      caCertPath: yup.string()
        .trim()
        .when([], {
          is: () => !isDesktopShell,
          then: (schema) => schema
            .required("CA certificate path is required")
            .matches(caCertPathPattern, "Provide a relative path ending in .crt or .pem without invalid characters"),
          otherwise: (schema) => schema
        }),
      playbackAcceleration: yup.string().oneOf(["auto", "software"]).required(),
      previewAcceleration: yup.string().oneOf(["auto", "software"]).required(),
      previewMemoryBudgetMiB: yup.number().integer().min(0).required(),
      previewWorkers: yup.number()
        .typeError("Preview workers must be a number")
        .integer("Preview workers must be a whole number")
        .min(1, "Preview workers must be at least 1")
        .required("Preview workers is required"),
      enablePreview: yup.boolean().required(),
      recastPreviewIntervalMs: yup.number().integer().min(0).required(),
      previewFramerate: yup.number()
        .typeError("Preview framerate must be a number")
        .min(0.1, "Preview framerate must be at least 0.1")
        .required("Preview framerate is required"),
      previewWidth: yup.number()
        .typeError("Preview width must be a number")
        .integer("Preview width must be a whole number")
        .min(64, "Preview width must be at least 64")
        .required("Preview width is required"),
      previewHeight: yup.number()
        .typeError("Preview height must be a number")
        .integer("Preview height must be a whole number")
        .min(64, "Preview height must be at least 64")
        .required("Preview height is required"),
      previewCaptureTimeoutMs: yup.number()
        .typeError("Capture timeout must be a number")
        .integer("Capture timeout must be a whole number")
        .min(100, "Capture timeout must be at least 100 ms")
        .required("Capture timeout is required"),
      previewTargetWarmupGraceMs: yup.number()
        .typeError("Warm-up grace must be a number")
        .integer("Warm-up grace must be a whole number")
        .min(0, "Warm-up grace must be zero or greater")
        .required("Warm-up grace is required"),
      previewBackoffMinMs: yup.number()
        .typeError("Backoff min must be a number")
        .integer("Backoff min must be a whole number")
        .min(100, "Backoff min must be at least 100 ms")
        .required("Backoff min is required"),
      previewBackoffMaxMs: yup.number()
        .typeError("Backoff max must be a number")
        .integer("Backoff max must be a whole number")
        .min(yup.ref("previewBackoffMinMs"), "Backoff max must be greater than or equal to backoff min")
        .required("Backoff max is required"),
      map: yup.object().shape({
        tileLayers: yup.string()
          .required("Tile layers are required")
          .test("is-json-array", "Tile layers must be a JSON array", (value) => isTileLayersJson(value)),
        enableOffline: yup.boolean().required()
      }),
      analytics: yup.object().shape({
        enabled: yup.boolean().required(),
        dsn: yup.string().trim(),
        host: yup.string().trim(),
        port: yup.number()
          .typeError("Analytics port must be a number")
          .integer("Analytics port must be a whole number")
          .min(1, "Analytics port must be between 1 and 65535")
          .max(65535, "Analytics port must be between 1 and 65535"),
        database: yup.string().trim(),
        user: yup.string().trim(),
        password: yup.string(),
        sslMode: yup.string().trim(),
        dataRetentionDays: yup.number()
          .typeError("Data retention must be a number of days")
          .integer("Data retention must be a whole number of days")
          .min(1, "Data retention must be at least 1 day")
          .max(3650, "Data retention cannot exceed 10 years"),
        dataCompressionDays: yup.number()
          .typeError("Data compression must be a number of days")
          .integer("Data compression must be a whole number of days")
          .min(0, "Data compression must be at least 0 days")
          .max(3650, "Data compression cannot exceed 10 years")
      }).test("analytics-required-fields", "Analytics connection requires host, port, database, and user when DSN is empty", function (value) {
        if (!value?.enabled) {
          return true;
        }

        const hasDSN = typeof value.dsn === "string" && value.dsn.trim().length > 0;
        if (hasDSN) {
          return true;
        }

        if (!value.host || value.host.trim().length === 0) {
          return this.createError({ path: `${this.path}.host`, message: "Host is required when DSN is empty" });
        }
        if (!value.port || value.port < 1 || value.port > 65535) {
          return this.createError({ path: `${this.path}.port`, message: "Analytics port must be between 1 and 65535" });
        }
        if (!value.database || value.database.trim().length === 0) {
          return this.createError({ path: `${this.path}.database`, message: "Database is required when DSN is empty" });
        }
        if (!value.user || value.user.trim().length === 0) {
          return this.createError({ path: `${this.path}.user`, message: "User is required when DSN is empty" });
        }

        if (value.dataRetentionDays === undefined || value.dataRetentionDays === null || value.dataRetentionDays < 1) {
          return this.createError({ path: `${this.path}.dataRetentionDays`, message: "Data retention must be at least 1 day" });
        }

        if (value.dataCompressionDays !== undefined && value.dataCompressionDays !== null && value.dataCompressionDays > value.dataRetentionDays) {
          return this.createError({ path: `${this.path}.dataCompressionDays`, message: "Data compression cannot exceed data retention" });
        }

        return true;
      })
    }),
    enableReinitialize: true,
    initialValues: initialConfig,
    onSubmit: async (values, helpers) => {
      setStatus({ saved: false, error: null });

      try {
        const payloadValues: RuntimeConfig = {
          ...values,
          accessTokenTtlMinutes: Number.isNaN(values.accessTokenTtlMinutes)
            ? defaultRuntimeConfig.accessTokenTtlMinutes
            : values.accessTokenTtlMinutes,
          caCertPath: values.caCertPath.trim(),
          previewMemoryBudgetMiB: values.previewMemoryBudgetMiB || defaultRuntimeConfig.previewMemoryBudgetMiB,
          previewWorkers: Number.isNaN(values.previewWorkers)
            ? defaultRuntimeConfig.previewWorkers
            : values.previewWorkers,
          recastPreviewIntervalMs: values.recastPreviewIntervalMs || defaultRuntimeConfig.recastPreviewIntervalMs,
          previewFramerate: Number.isNaN(values.previewFramerate)
            ? defaultRuntimeConfig.previewFramerate
            : values.previewFramerate,
          previewWidth: Number.isNaN(values.previewWidth)
            ? defaultRuntimeConfig.previewWidth
            : values.previewWidth,
          previewHeight: Number.isNaN(values.previewHeight)
            ? defaultRuntimeConfig.previewHeight
            : values.previewHeight,
          previewCaptureTimeoutMs: Number.isNaN(values.previewCaptureTimeoutMs)
            ? defaultRuntimeConfig.previewCaptureTimeoutMs
            : values.previewCaptureTimeoutMs,
          previewTargetWarmupGraceMs: Number.isNaN(values.previewTargetWarmupGraceMs)
            ? defaultRuntimeConfig.previewTargetWarmupGraceMs
            : values.previewTargetWarmupGraceMs,
          previewBackoffMinMs: Number.isNaN(values.previewBackoffMinMs)
            ? defaultRuntimeConfig.previewBackoffMinMs
            : values.previewBackoffMinMs,
          previewBackoffMaxMs: Number.isNaN(values.previewBackoffMaxMs)
            ? defaultRuntimeConfig.previewBackoffMaxMs
            : values.previewBackoffMaxMs
        };
        const { serverName: _serverName, map, analytics, ...restPayload } = payloadValues;
        const { offlineLicensed: _offlineLicensed, ...mapPayload } = map;
        const analyticsPayload = {
          ...analytics,
          dsn: analytics.dsn.trim(),
          host: analytics.host.trim(),
          database: analytics.database.trim(),
          user: analytics.user.trim(),
          password: analytics.password.trim(),
          sslMode: analytics.sslMode.trim(),
          dataRetentionDays: analytics.dataRetentionDays,
          dataCompressionDays: analytics.dataCompressionDays
        };
        const configPayload = { ...restPayload, map: mapPayload, analytics: analyticsPayload };

        const response = await authorizedFetch("/api/config", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(configPayload)
        });

        const payload = (await response.json().catch(() => null)) as {
          config?: Partial<RuntimeConfig>;
          error?: string;
          details?: string;
        } | null;

        if (!response.ok || !payload?.config) {
          const detail = typeof payload?.details === "string" ? payload.details.trim() : "";
          const baseError = payload?.error ?? "Failed to save configuration";
          throw new Error(detail.length > 0 ? `${baseError}: ${detail}` : baseError);
        }

        const normalizedConfig = normalizeRuntimeConfig(payload.config);
        setInitialConfig(normalizedConfig);
        helpers.resetForm({ values: normalizedConfig });
        setStatus({ saved: true, error: null });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to save configuration";

        if (message.includes(userManagementLicenseError)) {
          await helpers.setFieldValue("enableUserManagement", false, false);
          await helpers.setFieldValue("registrationOpen", false, false);
        }
        if (message.includes(errorDetectionLicenseError)) {
          await helpers.setFieldValue("enableErrorDetection", false, false);
        }
        if (message.includes(offlineMapLicenseError)) {
          await helpers.setFieldValue("map.enableOffline", false, false);
        }
        if (message.includes(analyticsLicenseError)) {
          await helpers.setFieldValue("analytics.enabled", false, false);
        }

        setStatus({
          saved: false,
          error: message
        });
      } finally {
        helpers.setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    let cancelled = false;
    const loadConfig = async () => {
      try {
        const response = await authorizedFetch("/api/config");
        const payload = (await response.json().catch(() => null)) as {
          config?: Partial<RuntimeConfig>;
          error?: string;
        } | null;

        if (!response.ok || !payload?.config) {
          throw new Error(payload?.error ?? "Failed to load configuration");
        }

        if (!cancelled) {
          setInitialConfig(normalizeRuntimeConfig(payload.config));
          setStatus({ saved: false, error: null });
        }
      } catch (error) {
        if (!cancelled) {
          setStatus({ saved: false, error: error instanceof Error ? error.message : "Unable to load configuration" });
        }
      }
    };

    void loadConfig();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  useEffect(() => {
    if (formik.dirty) {
      setStatus((prev) => (prev.saved ? { ...prev, saved: false } : prev));
    }
  }, [formik.dirty]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleOpenConfigServerHelp = () => {
    if (typeof window === "undefined") {
      return;
    }
    void openExternalPath("/help/user-guide/config-server.html");
  };

  const canRestartServer = !userManagementEnabled || (user?.role ?? "").toLowerCase() === "admin";

  const handleServerRestart = async () => {
    if (!canRestartServer) {
      return;
    }
    setRestartStatus({ pending: true, error: null });
    try {
      const response = await authorizedFetch("/api/server/restart", { method: "POST" });
      const payload = (await response.json().catch(() => null)) as { status?: string; error?: string } | null;

      if (!response.ok) {
        throw new Error(payload?.error ?? "Failed to restart the server");
      }

      setRestartStatus({ pending: false, error: null });
    } catch (error) {
      setRestartStatus({
        pending: false,
        error: error instanceof Error ? error.message : "Unable to restart the server"
      });
    }
  };

  const requestRestartConfirmation = () => {
    if (restartStatus.pending) {
      return;
    }
    setRestartConfirmOpen(true);
  };

  const handleRestartConfirm = async () => {
    setRestartConfirmOpen(false);
    await handleServerRestart();
  };

  const handleRestartCancel = () => {
    setRestartConfirmOpen(false);
  };

  const analyticsEnabled = formik.values.analytics.enabled;
  const tabItems = [
    {
      label: "Server",
      content: (
        <ServerForm
          formik={formik}
          defaultServerName={defaultRuntimeConfig.serverName}
          canRestartServer={canRestartServer}
          isDesktopShell={isDesktopShell}
          onRequestRestart={requestRestartConfirmation}
          restartStatus={restartStatus}
        />
      )
    },
    {
      label: "License",
      content: (
        <LicenseCtrl
          value={formik.values.token}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      )
    },
    { label: "WebRTC", content: <WebRtcForm isDesktopShell={isDesktopShell} /> },
    { label: "Map", content: <MapForm /> },
    { label: "RTSP Server", content: <RTSPServerForm /> },
    {
      label: "Preview",
      content: (
        <PreviewCfgForm
          defaults={{
            enablePreview: defaultRuntimeConfig.enablePreview,
            previewWorkers: defaultRuntimeConfig.previewWorkers,
            previewAcceleration: defaultRuntimeConfig.previewAcceleration,
            previewMemoryBudgetMiB: defaultRuntimeConfig.previewMemoryBudgetMiB,
            previewFramerate: defaultRuntimeConfig.previewFramerate,
            recastPreviewIntervalMs: defaultRuntimeConfig.recastPreviewIntervalMs,
            previewWidth: defaultRuntimeConfig.previewWidth,
            previewHeight: defaultRuntimeConfig.previewHeight,
            previewCaptureTimeoutMs: defaultRuntimeConfig.previewCaptureTimeoutMs,
            previewTargetWarmupGraceMs: defaultRuntimeConfig.previewTargetWarmupGraceMs,
            previewBackoffMinMs: defaultRuntimeConfig.previewBackoffMinMs,
            previewBackoffMaxMs: defaultRuntimeConfig.previewBackoffMaxMs
          }}
        />
      )
    },
    ...(!isDesktopShell ? [{ label: "PWA", content: <PwaForm /> }] : []),
    ...(!isDesktopShell ? [{ label: "Security", content: <SecurityForm /> }] : []),
    ...(analyticsEnabled ? [{ label: "Analytics", content: <AnalyticsForm /> }] : []),
    { label: "About", content: <AboutForm /> }
  ];

  useEffect(() => {
    if (tab >= tabItems.length) {
      setTab(Math.max(0, tabItems.length - 1));
    }
  }, [tab, tabItems.length]);

  return (
    <Box component="section" sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
      <Stack spacing={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <SettingsIcon sx={{ color: "text.secondary" }} />
          <Typography variant="h5" sx={{ m: 0 }}>
            Server configuration
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Help">
            <IconButton
              size="small"
              onClick={handleOpenConfigServerHelp}
              aria-label="Open server configuration help"
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Paper sx={{ p: 3 }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            aria-label="Configuration tabs"
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            {tabItems.map((item, index) => (
              <Tab
                key={item.label}
                label={item.label}
                id={`config-tab-${index}`}
                aria-controls={`config-tabpanel-${index}`}
                sx={{ minWidth: 0, px: 1.25 }}
              />
            ))}
          </Tabs>
          <FormikProvider value={formik}>
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ display: "grid", gap: 2 }}>
              {tabItems.map((item, index) => (
                <TabPanel value={tab} index={index} key={item.label}>
                  {item.content}
                </TabPanel>
              ))}
              <Button
                type="submit"
                variant="contained"
                disabled={!formik.dirty || formik.isSubmitting}
                sx={{ justifySelf: "start" }}
              >
                Save
              </Button>
              {status.error && <Alert severity="error">{status.error}</Alert>}
              {status.saved && !status.error && <Alert severity="success">Configuration saved</Alert>}
            </Box>
          </FormikProvider>
          {!isDesktopShell && (
            <Dialog open={restartConfirmOpen} onClose={handleRestartCancel}>
              <DialogTitle>Restart server</DialogTitle>
              <DialogContent>
                Restarting disconnects all viewers briefly while services come back online. Continue?
              </DialogContent>
              <DialogActions>
                <Button onClick={handleRestartCancel}>Cancel</Button>
                <Button onClick={handleRestartConfirm} variant="contained">Restart</Button>
              </DialogActions>
            </Dialog>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default ConfigPage;
